import React, { Suspense, useEffect, useState } from "react";
import "./App.css";
import axios from "axios";
import appConfig from "../../constants/config";
import LoadingSpinner from "../../components/Loader/Loader";
import { BrowserRouter, useParams, useSearchParams } from "react-router-dom";
import MasterContainer from "../Layout/Layout";
import NotFoundPage from "./NotFound";
import { Provider } from "react-redux";
import reduxStore from "../../utils/reduxStore";
import { Routes, Route } from "react-router-dom";
import * as serviceWorkerRegistration from "../../serviceWorkerRegistration";
import { useAuth } from "../../components/AuthProvider/useAuth";

// Lazy load pages
const QuickAddPage = React.lazy(() => import("../QuickAdd/QuickAdd"));
const ClaimItems = React.lazy(() => import("../ClaimItems/ClaimItems"));
const ClaimDetailUrl = React.lazy(() => import("../ClaimItems/ClaimDetail"));
const ExpenseReportPage = React.lazy(() => import("../ExpReport/ExpReport"));
const PreApprovalForm = React.lazy(() => import("../PreApprovalForm/PreApprovalForm"));
const PreApprovalFormDetail = React.lazy(() => import("../PreApprovalForm/PreApprovalFormDetail"));
const RoleAssignment = React.lazy(() => import("../RoleAssignment/RoleAssignment"));
const RoleAssignment2 = React.lazy(() => import("../RoleAssignment/RoleAssignment2"));
const MassDownloadList = React.lazy(() => import("../MassDownload/MassDownloadlist"));
const FileDownload = React.lazy(() => import("../FileDownload/FileDownload"));
const WorkflowMatrix = React.lazy(() => import("../Admin/WorkflowMatrix"));
const CorporateBilling = React.lazy(() => import("../CorporateBilling/CorporateBilling"));
const CorporateBillTracking = React.lazy(() => import("../CorporateBillingAdmin/CorporateBillTracking"));
const ExcelFileUpload = React.lazy(() => import("../CorporateBillingAdmin/ExcelFileUpload"));
const Camera = React.lazy(() => import("../../components/Camera/Camera"));

const basename = appConfig.connectionString.appBaseName;
const wsConnectionString = appConfig.connectionString.ws;

// Saber Amendment 20260817 : third-party analytics/tracking cookies set at the parent dksh.com
// domain (Google Analytics, Beamer, Microsoft Clarity, Heap Analytics) can accumulate over time
// across dksh.com properties and grow the Cookie header large/unusual enough to trip Azure Front
// Door's WAF (403 "The request is blocked.") on later requests to eec.dksh.com - seen so far only
// in Edge profiles with a longer browsing history on dksh.com. This cannot prevent a block on the
// very first request (our JS never runs if that request itself is blocked), but it proactively
// clears these cookies on every successful load so they cannot keep accumulating, reducing the
// chance of hitting the WAF threshold again on a subsequent visit.
const THIRD_PARTY_TRACKING_COOKIE_PATTERNS = [/^_ga/, /^_gid$/, /^_gat/, /^_clck$/, /^_clsk$/, /^_BEAMER_/, /^_hp2_/];

const clearThirdPartyTrackingCookies = () => {
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name || !THIRD_PARTY_TRACKING_COOKIE_PATTERNS.some((pattern) => pattern.test(name))) {
      return;
    }
    const expire = "expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    document.cookie = `${name}=;${expire}`;
    document.cookie = `${name}=;${expire};domain=dksh.com`;
    document.cookie = `${name}=;${expire};domain=.dksh.com`;
  });
};

function App() {
  const [isReady, setIsReady] = useState<boolean>(
    localStorage.getItem("objRequestor") != null
  );
  const { loadEmployeeProfile, keyRefresh } = useAuth();

  useEffect(() => {
    // This function will be called on component mount (similar to page load)
    clearThirdPartyTrackingCookies();
    loadEmployeeProfile();
    setIsReady(true);

    let refreshing = false;

    // Register service worker with update notification logic
    serviceWorkerRegistration.register({
      onUpdate: (registration: any) => {
        if (registration && registration.waiting) {
          // Listen for changes in the controlling service worker
          if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener("controllerchange", () => {
              if (!refreshing) {
                window.location.reload();
                refreshing = true;
              }
            });
          }
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          // Notify user of the new version
          // const userConsent = window.confirm(
          //   "A new version of the app is available. Would you like to update now?"
          // );

          // if (userConsent) {
          //   // Tell the service worker to activate immediately
          //   registration.waiting.postMessage({ type: "SKIP_WAITING" });
          // }
        }
      },
    });

    const pwaSessionKey = 'eec_pwa_active';
    localStorage.setItem(pwaSessionKey, 'true');
  
    const handleUnload = () => {
      localStorage.removeItem(pwaSessionKey);
    };
  
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === pwaSessionKey && e.newValue === null) {
        alert('Your session has been closed or moved. Redirecting to EEC Plus.');
        window.location.href = appConfig.LegacySystemUrl;
      }
    };
  
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('storage', handleStorageChange);
  
    return () => {
      handleUnload();
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('storage', handleStorageChange);
    };

  }, []);

  const selectedLanguage = window.sessionStorage.getItem("selectedLanguage");
  if (selectedLanguage === null || selectedLanguage === undefined) {
    window.sessionStorage.setItem("selectedLanguage", "en");
  }

  // Default Set View Screen Size to 80%
  // (document.body.style as any).zoom = "100%";
  // if (window.innerWidth >= 767 && window.innerWidth <= 1500) {
  //   (document.body.style as any).zoom = "95%";
  // } else {
  //   (document.body.style as any).zoom = "100%";
  // }

  //set scroll-y overflow hidden
  document.documentElement.style.setProperty("--overflow", "hidden");

  //call access token api
  const getAccessToken = async () => {
    let accessToken = "";

    const tokenEndpoint = wsConnectionString + "/token";
    const grantType = "password";
    const username = appConfig.connectionString.WSUserName;
    const password = appConfig.connectionString.WSPWD;

    try {
      const response = await axios.post(
        tokenEndpoint,
        new URLSearchParams({
          grant_type: grantType,
          username: username,
          password: password,
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.status === 200) {
        accessToken = response.data.access_token;
        localStorage.setItem("token", accessToken);
        await loadEmployeeProfile();
        setIsReady(true);
      } else {
        console.error("Failed to get access token:", response.status);
      }
    } catch (error: any) {
      console.error("Error:", error.message);
    }
  };
  const ClaimItemsDetailWrapper = (props: any) => {
    const { claimid } = useParams();
    return <ClaimDetailUrl key={claimid} {...props} />;
  };
  // Saber Amendment 20260817 : INC-1022730 - SAP posting tools and the claim enquiry SQL procs generate
  // legacy file-download links against the root path (https://eec.dksh.com/?down_guid=X&claimid=Y) rather
  // than /FileDownload/GetFile, so the root route must also serve FileDownload when down_guid is present.
  const RootRouteHandler = () => {
    const [searchParams] = useSearchParams();
    return searchParams.get("down_guid") ? <FileDownload /> : <QuickAddPage />;
  };

  return (
    <Provider store={reduxStore}>
      <BrowserRouter basename={basename}>
        <MasterContainer key={keyRefresh}>
          <Suspense fallback={<LoadingSpinner loading={isReady} />}>
            <Routes>
              <Route path="/" element={<RootRouteHandler />} />
              <Route path="/ClaimItems/ClaimItems" element={<ClaimItems />} />
              <Route
                path="/ClaimItems/ClaimItems/:claimid"
                element={<ClaimItemsDetailWrapper />}
              />
              <Route path="/QuickAdd" element={<QuickAddPage />} />
              <Route
                path="/ExpReport/ExpReport"
                element={<ExpenseReportPage />}
              />
              <Route path="/Form/Form" element={<PreApprovalForm />} />
              <Route path="/RoleAssignment/RoleAssignment" element={<RoleAssignment />} />
              <Route path="/RoleAssignment/RoleAssignment2" element={<RoleAssignment2 />} />
              <Route path="/Admin/WorkflowMatrix" element={<WorkflowMatrix />} />
              <Route path="/CorporateBilling/CorporateBilling" element={<CorporateBilling />} />
              <Route path="/CorporateBillingAdmin/CorporateBillTracking" element={<CorporateBillTracking />} />
              <Route path="/CorporateBillingAdmin/ExcelFileUpload" element={<ExcelFileUpload />} />
              <Route path="/Form/Form/:id" element={<PreApprovalFormDetail />}/>
              <Route path="/FileDownload/MassDownLoadList" element={<MassDownloadList />} />
              <Route path="/FileDownload/GetFile" element={<FileDownload />} />
              <Route path="/Camera" element={<Camera />} />
              <Route path="/not-found" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />}></Route>
            </Routes>
          </Suspense>
        </MasterContainer>
      </BrowserRouter>
    </Provider>
  );
}

export default App;